import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  ManageAccounts as ManageAccountsIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';
import { FILM_TIER_NAMES, FILM_TIERS, SUBSCRIPTION_STATUS } from '../../lib/filmSubscriptionTiers';
import userManagementApi from '../../lib/services/userManagementApi';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = () => {
  const { isSuperAdmin } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [lastFocusedElement, setLastFocusedElement] = useState(null);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);
  const [subscriptionUser, setSubscriptionUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    role: 'user',
    isActive: true,
    sendInvitation: true,
    isFilmAdmin: false
  });

const [subscriptionForm, setSubscriptionForm] = useState({
  tier: FILM_TIERS.FREE,
  status: SUBSCRIPTION_STATUS.ACTIVE,
  enterpriseCompanyId: '',
  startedAt: '',
  expiresAt: '',
  isEnterpriseAdmin: false
});

  const subscriptionStatusLabels = {
    [SUBSCRIPTION_STATUS.ACTIVE]: 'Active',
    [SUBSCRIPTION_STATUS.TRIAL]: 'Trial',
    [SUBSCRIPTION_STATUS.EXPIRED]: 'Expired',
    [SUBSCRIPTION_STATUS.CANCELLED]: 'Cancelled'
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userManagementApi.fetchUsers();
      const fetchedUsers = response.users || [];
      setAllUsers(fetchedUsers);
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm) {
      setUsers(allUsers);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const filtered = allUsers.filter(user => 
      user.email?.toLowerCase().includes(lowerTerm) ||
      user.displayName?.toLowerCase().includes(lowerTerm)
    );
    setUsers(filtered);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateUser = (event) => {
    setLastFocusedElement(event?.currentTarget || document.activeElement);
    event?.currentTarget?.blur();
    setDialogMode('create');
    setFormData({
      email: '',
      displayName: '',
      role: 'user',
      isActive: true,
      sendInvitation: true,
      isFilmAdmin: false
    });
    setSelectedUser(null);
    setOpenDialog(true);
  };

  const handleEditUser = (event, user) => {
    setLastFocusedElement(event?.currentTarget || document.activeElement);
    event?.currentTarget?.blur();
    setDialogMode('edit');
    setFormData({
      email: user.email,
      displayName: user.displayName || '',
      role: user.role || 'user',
      isActive: user.isActive !== false,
      sendInvitation: false,
      isFilmAdmin: user.isFilmAdmin || false
    });
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const buildUpdatePayload = () => {
    const payload = {
      displayName: formData.displayName || undefined,
      role: formData.role,
      isActive: formData.isActive,
    };
    
    // Always include isFilmAdmin if user is super admin (including when it's false)
    if (isSuperAdmin()) {
      payload.isFilmAdmin = Boolean(formData.isFilmAdmin);
    }
    
    return payload;
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      if (dialogMode === 'create') {
        // Build create payload, only include isFilmAdmin if super_admin
        const createPayload = {
          ...formData,
          ...(isSuperAdmin() ? { isFilmAdmin: formData.isFilmAdmin } : {})
        };
        await userManagementApi.createUser(createPayload);
      } else if (selectedUser) {
        const payload = buildUpdatePayload();
        console.log('Saving user with payload:', payload);
        await userManagementApi.updateUser(selectedUser.uid, payload);
      }
 
      setMessage({ 
        type: 'success', 
        text: `User ${dialogMode === 'create' ? 'created' : 'updated'} successfully!` 
      });
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
      return;
    }

    setSaving(true);
    try {
      await userManagementApi.deleteUser(user.uid);
 
      setMessage({ type: 'success', text: 'User deleted successfully!' });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    setSaving(true);
    try {
      await userManagementApi.updateUser(user.uid, { isActive: !user.isActive });
 
      setMessage({ type: 'success', text: `User ${user.isActive ? 'deactivated' : 'activated'} successfully!` });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return 'error';
      case 'user':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return <AdminIcon />;
      case 'user':
        return <PersonIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => {
      lastFocusedElement?.focus?.();
    }, 0);
  };

  const formatDateInput = (value) => {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : (value.toDate ? value.toDate() : new Date(value));
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const handleOpenSubscriptionDialog = (user) => {
    setSubscriptionUser(user);
    setSubscriptionForm({
      tier: user.filmSubscriptionTier || FILM_TIERS.FREE,
      status: user.filmSubscriptionStatus || SUBSCRIPTION_STATUS.ACTIVE,
    enterpriseCompanyId: user.enterpriseCompanyId || user.filmCompanyId || '',
      startedAt: formatDateInput(user.filmSubscriptionStartedAt),
      expiresAt: formatDateInput(user.filmSubscriptionExpiresAt),
    isEnterpriseAdmin: user.isEnterpriseAdmin || user.isFilmEnterpriseAdmin || false
    });
    setSubscriptionDialogOpen(true);
  };

  const handleCloseSubscriptionDialog = () => {
    if (subscriptionSaving) return;
    setSubscriptionDialogOpen(false);
    setSubscriptionUser(null);
  };

  const handleSubscriptionFieldChange = (field, value) => {
    setSubscriptionForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubscriptionSave = async () => {
    if (!subscriptionUser) return;
    setSubscriptionSaving(true);
    try {
      const payload = {
        filmSubscriptionTier: subscriptionForm.tier,
        filmSubscriptionStatus: subscriptionForm.status,
        filmSubscriptionExpiresAt: subscriptionForm.expiresAt ? new Date(subscriptionForm.expiresAt).toISOString() : null,
        filmSubscriptionStartedAt: subscriptionForm.startedAt ? new Date(subscriptionForm.startedAt).toISOString() : null,
        enterpriseCompanyId: subscriptionForm.enterpriseCompanyId || null,
        filmCompanyId: subscriptionForm.enterpriseCompanyId || null,
        isEnterpriseAdmin: subscriptionForm.isEnterpriseAdmin,
        isFilmEnterpriseAdmin: subscriptionForm.isEnterpriseAdmin
      };

      await userManagementApi.updateUserSubscription(subscriptionUser.uid, payload);
      setMessage({ type: 'success', text: 'Subscription updated successfully!' });
      handleCloseSubscriptionDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error updating subscription:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case SUBSCRIPTION_STATUS.ACTIVE:
        return 'success';
      case SUBSCRIPTION_STATUS.TRIAL:
        return 'info';
      case SUBSCRIPTION_STATUS.EXPIRED:
        return 'warning';
      case SUBSCRIPTION_STATUS.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  const tierChipColors = {
    film_enterprise: 'error',
    film_professional: 'primary',
    film_basic: 'info',
    film_free: 'default'
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, color: brandColors.primary, fontWeight: 600 }}>
          <AdminIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage user accounts, roles, and permissions for the platform.
        </Typography>
      </Box>

      {/* Message Alert */}
      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }} 
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchUsers}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleCreateUser}
                  sx={{ bgcolor: brandColors.primary }}
                >
                  Add User
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            Users ({filteredUsers.length})
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Film Tier</TableCell>
                  <TableCell>Subscription Status</TableCell>
                  <TableCell>Enterprise Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.uid} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: brandColors.primary }}>
                          {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {user.displayName || 'No Name'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          icon={getRoleIcon(user.role)}
                          label={user.role || 'user'}
                          color={getRoleColor(user.role)}
                          size="small"
                          variant="outlined"
                        />
                        {user.isFilmAdmin && (
                          <Chip
                            label="Film Admin"
                            color="primary"
                            size="small"
                            variant="filled"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={FILM_TIER_NAMES[user.filmSubscriptionTier] || FILM_TIER_NAMES[FILM_TIERS.FREE]}
                        size="small"
                        color={tierChipColors[user.filmSubscriptionTier] || tierChipColors[FILM_TIERS.FREE]}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subscriptionStatusLabels[user.filmSubscriptionStatus] || 'Unknown'}
                        size="small"
                        color={getSubscriptionStatusColor(user.filmSubscriptionStatus)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {(user.enterpriseCompanyId || user.filmCompanyId) ? (
                        <Chip
                          label="Enterprise"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Individual
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={user.isActive !== false}
                              onChange={() => handleToggleUserStatus(user)}
                              disabled={saving}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {user.isActive !== false ? (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                              ) : (
                                <CancelIcon sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
                              )}
                              <Typography variant="body2">
                                {user.isActive !== false ? 'Active' : 'Inactive'}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={(event) => handleEditUser(event, user)}
                            disabled={saving}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Manage Subscription">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenSubscriptionDialog(user)}
                            disabled={saving}
                          >
                            <ManageAccountsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user)}
                            disabled={saving}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredUsers.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? 'Try adjusting your search terms' : 'Create your first user to get started'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New User' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={dialogMode === 'edit'}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    label="Role"
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="super_admin">Super Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active User"
                />
              </Grid>
              {isSuperAdmin() && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isFilmAdmin || false}
                        onChange={(e) => handleInputChange('isFilmAdmin', e.target.checked)}
                      />
                    }
                    label="Film Admin"
                  />
                </Grid>
              )}
              {dialogMode === 'create' && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.sendInvitation}
                        onChange={(e) => handleInputChange('sendInvitation', e.target.checked)}
                      />
                    }
                    label="Send invitation email"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={saving || !formData.email}
            sx={{ bgcolor: brandColors.primary }}
          >
            {saving ? <CircularProgress size={20} /> : (dialogMode === 'create' ? 'Create User' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={subscriptionDialogOpen} onClose={handleCloseSubscriptionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Film Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {subscriptionUser?.displayName || subscriptionUser?.email}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Film Tier</InputLabel>
                  <Select
                    value={subscriptionForm.tier}
                    label="Film Tier"
                    onChange={(e) => handleSubscriptionFieldChange('tier', e.target.value)}
                  >
                    {Object.entries(FILM_TIER_NAMES).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Subscription Status</InputLabel>
                  <Select
                    value={subscriptionForm.status}
                    label="Subscription Status"
                    onChange={(e) => handleSubscriptionFieldChange('status', e.target.value)}
                  >
                    {Object.entries(subscriptionStatusLabels).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subscription Start"
                  type="date"
                  value={subscriptionForm.startedAt}
                  onChange={(e) => handleSubscriptionFieldChange('startedAt', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subscription Expiration"
                  type="date"
                  value={subscriptionForm.expiresAt}
                  onChange={(e) => handleSubscriptionFieldChange('expiresAt', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Enterprise Company ID"
                  placeholder="Enter enterprise company ID (optional)"
                  value={subscriptionForm.enterpriseCompanyId}
                  onChange={(e) => handleSubscriptionFieldChange('enterpriseCompanyId', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={subscriptionForm.isEnterpriseAdmin}
                      onChange={(e) => handleSubscriptionFieldChange('isEnterpriseAdmin', e.target.checked)}
                    />
                  )}
                  label="Enterprise Admin"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubscriptionDialog} disabled={subscriptionSaving}>Cancel</Button>
          <Button
            onClick={handleSubscriptionSave}
            variant="contained"
            disabled={subscriptionSaving}
            sx={{ bgcolor: brandColors.primary }}
          >
            {subscriptionSaving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
